import type {
  MonthlyClose,
  MonthlyCloseOpenItems,
  PlatformMetrics,
  Transaction,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

export interface ReviewQueueItem {
  id: string;
  organizationId: string;
  year: number;
  month: number;
  status: MonthlyClose['status'];
  totalRevenue: string | null;
  totalExpenses: string | null;
  netProfit: string | null;
  transactionCount: number | null;
  aiAutomationRate: number | null;
  flagCount: number | null;
  reviewerId: string | null;
  reviewStartedAt: string | null;
  reviewMinutes: number | null;
  summary: string | null;
  summaryDeliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  reviewer: {
    id: string;
    fullName: string;
  } | null;
}

export interface ReviewOrg {
  id: string;
  name: string;
  slug: string;
  monthlyCloseCount: number;
  pendingReviewCount: number;
}

export interface OrgTransactionsResponse {
  transactions: Transaction[];
  categories: import('@eriko/shared-types').Category[];
}

export interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  createdAt: string;
  ownerEmail: string | null;
  connections: { provider: 'QBO' | 'XERO'; isActive: boolean }[];
  jurisdiction: string;
  // Billing
  trialEndsAt: string | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialExtendedAt: string | null;
  trialExtendedByUserId: string | null;
  trialExtendedReason: string | null;
}

export async function getReviewQueue(): Promise<ReviewQueueItem[]> {
  const res = await apiClient.get<ReviewQueueItem[]>(ENDPOINTS.review.queue);
  return res.data;
}

export async function getReviewOrgs(): Promise<ReviewOrg[]> {
  const res = await apiClient.get<ReviewOrg[]>(ENDPOINTS.review.orgs);
  return res.data;
}

export async function getOrgTransactions(
  orgId: string,
  monthlyCloseId?: string,
): Promise<OrgTransactionsResponse> {
  const params = new URLSearchParams();
  if (monthlyCloseId) params.set('monthlyCloseId', monthlyCloseId);
  const qs = params.toString();
  const res = await apiClient.get<OrgTransactionsResponse>(
    `${ENDPOINTS.review.orgTransactions(orgId)}${qs ? `?${qs}` : ''}`,
  );
  return res.data;
}

export async function getOrgMonthlyCloses(orgId: string): Promise<MonthlyClose[]> {
  const res = await apiClient.get<MonthlyClose[]>(ENDPOINTS.review.orgMonthlyCloses(orgId));
  return res.data;
}

export async function getCloseOpenItems(closeId: string): Promise<MonthlyCloseOpenItems> {
  const res = await apiClient.get<MonthlyCloseOpenItems>(
    ENDPOINTS.monthlyClose.openItems(closeId),
  );
  return res.data;
}

export async function getPlatformMetrics(): Promise<PlatformMetrics> {
  const res = await apiClient.get<PlatformMetrics>(ENDPOINTS.metrics.platform);
  return res.data;
}

export async function getOrgDetail(orgId: string): Promise<OrgDetail> {
  const res = await apiClient.get<OrgDetail>(ENDPOINTS.review.orgDetail(orgId));
  return res.data;
}

export async function deleteMonthlyClose(
  orgId: string,
  closeId: string,
): Promise<{ transactions: number; ledgerEntries: number }> {
  const res = await apiClient.delete<{ transactions: number; ledgerEntries: number }>(
    ENDPOINTS.review.deleteMonthlyClose(orgId, closeId),
  );
  return res.data;
}

export async function updateTransaction(
  id: string,
  data: {
    categoryId?: string;
    isReviewed?: boolean;
    flags?: Array<{ id: string; isResolved: boolean; resolution?: string }>;
  },
): Promise<Transaction> {
  const res = await apiClient.patch<Transaction>(ENDPOINTS.review.updateTransaction(id), data);
  return res.data;
}

export async function updateMonthlyCloseStatus(
  id: string,
  data: { status: MonthlyClose['status']; force?: boolean },
): Promise<MonthlyClose> {
  const res = await apiClient.patch<MonthlyClose>(ENDPOINTS.monthlyClose.update(id), data);
  return res.data;
}
